
import store from '@/store'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToolbar,
} from '@ionic/vue';
import HomeButtonLayout from '@/layouts/home/ButtonLayout.vue'
import HomeMapLayout from '@/layouts/home/MapLayout.vue'
import HomeWelcomeLayout from '@/layouts/home/WelcomeLayout.vue'
import jsQR from "jsqr";
import {defineComponent, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";

const pwaConfig = ref<any>({})
const showButtonLayout = ref<boolean>(false)
const showWelcomeLayout = ref<boolean>(false)
const showSearchField = ref<any>(false)
const showQrScanner = ref<any>(false)

let qrCanvasContext: CanvasRenderingContext2D | undefined
let qrCanvasElement: HTMLCanvasElement | undefined
let qrFileInputElement: HTMLInputElement | undefined
let qrVideoElement: HTMLVideoElement | undefined
let qrVideoStream: MediaStream | undefined

const offerNavigationToQrCodeUrl = (qrCodeData: any) => {
  if (String(qrCodeData).startsWith('http')) {
    if (confirm(`Would you like to go to that URL?\n${qrCodeData}`)) {
      window.location = qrCodeData
    }
  } else {
    console.log(qrCodeData)
    alert('That QR code did not have a URL in it')
  }
}

let actOnQrCodeData = offerNavigationToQrCodeUrl

const getQrCodeFromCanvas = (
    canvasContext: CanvasRenderingContext2D,
    canvasElement: HTMLCanvasElement
) => {
  const imageData = canvasContext.getImageData(
      0,
      0,
      canvasElement.width,
      canvasElement.height
  )
  return jsQR(imageData.data, imageData.width, imageData.height)
}

const turnOffQrScanner = () => {
  showQrScanner.value = false
  qrVideoStream?.getVideoTracks().forEach(videoTrack => videoTrack.stop())
  qrVideoStream = undefined
}

const scanVideoFrameForQrCode = () => {
  console.log('scanVideoFrameForQrCode') // TEMP
  if (!showQrScanner.value) {
    return
  }

  if (!qrCanvasElement) {
    console.error('No QR code canvas element available')
    return
  } else if (!qrCanvasContext) {
    console.error('No QR code canvas context available')
    return
  } else if (!qrVideoElement) {
    console.error('No QR code video element available')
    return
  }

  if (qrVideoElement.readyState === qrVideoElement.HAVE_ENOUGH_DATA) {
    console.log('HAVE_ENOUGH_DATA') // TEMP
    qrCanvasElement.width = qrVideoElement.clientWidth
    qrCanvasElement.height = qrVideoElement.clientHeight

    qrCanvasContext.drawImage(
        qrVideoElement,
        0,
        0,
        qrCanvasElement.width,
        qrCanvasElement.height
    )

    const code = getQrCodeFromCanvas(qrCanvasContext, qrCanvasElement)
    console.log('code:', code) // TEMP
    if (code) {
      turnOffQrScanner()
      actOnQrCodeData(code.data)
    } else {
      requestAnimationFrame(scanVideoFrameForQrCode)
    }
  } else {
    console.log('! HAVE_ENOUGH_DATA') // TEMP
    requestAnimationFrame(scanVideoFrameForQrCode)
  }
}

const showConfiguredHomeScreenLayout = async () => {
  const config = await store.getPwaConfig()
  if (config.home_screen_layout === 'welcome_layout') {
    showWelcomeLayout.value = true
  }
}

const turnOnQrScanner = async (returnQrCodeData: (qrCodeData: any) => void) => {
  actOnQrCodeData = returnQrCodeData
  try {
    qrVideoStream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' }
    })
    if (qrVideoElement) {
      qrVideoElement.srcObject = qrVideoStream
      qrVideoElement.play()
      requestAnimationFrame(scanVideoFrameForQrCode)
    } else {
      console.error('Could not find the QR code video element on the page')
    }
  } catch (error: any) {
    turnOffQrScanner()
    console.error('Unable to get video stream from camera', error)
    qrFileInputElement?.click() // If the camera didn't work, try the file input.
  }
}

export default defineComponent({
  name: 'Home',
  components: {
    HomeButtonLayout,
    HomeMapLayout,
    HomeWelcomeLayout,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonMenuButton,
    IonPage,
    IonTitle,
    IonToolbar,
  },
  computed: {
    insideSubmap(): boolean {
      return !!store.state.selectedSubmap
    },
    isShowingConfiguredHomeScreenLayout(): boolean {
      const config = pwaConfig.value
      if (config.home_screen_layout === 'welcome_layout') {
        return showWelcomeLayout.value
      }
      return false
    },
  },
  mounted() {
    qrVideoElement = document.getElementById('qr-scanner-video') as HTMLVideoElement || undefined
    qrFileInputElement = document.getElementById('qr-scanner-file-input') as HTMLInputElement || undefined
    qrCanvasElement = document.getElementById('qr-scanner-canvas') as HTMLCanvasElement || undefined
    if (qrCanvasElement) {
      qrCanvasContext = qrCanvasElement.getContext('2d') || undefined
    }
  },
  props: {
    queryStringPointGuid: String,
    queryStringSubmapGuid: String,
  },
  setup(props) {
    const router = useRouter()
    const route = useRoute()

    store.getPwaConfig().then(async config => {
      pwaConfig.value = config

      // If there is a point GUID in the query string (?pid=), redirect to that point.
      if (props.queryStringPointGuid) {
        const point = await store.getPointOnceLoaded(props.queryStringPointGuid)
        if (point.parentSubmap) {
          router.push({ name: 'point-in-submap', params: {
            pointGuid: props.queryStringPointGuid,
            submapGuid: point.parentSubmap,
          } })
        } else {
          router.push({ name: 'point', params: {
            pointGuid: props.queryStringPointGuid
          } })
        }
      } else if (props.queryStringSubmapGuid) {
        router.push({ name: 'submap', params: {
          submapGuid: props.queryStringSubmapGuid
        } })
      } else if (route.name === 'home') {
        showConfiguredHomeScreenLayout()
      }
    })

    const toggleSearchField = () => {
      showSearchField.value = !showSearchField.value
      if (showSearchField.value) {
        setTimeout(() => {
          const searchField = document.getElementById('search-field') as HTMLInputElement
          searchField && searchField.focus()
        })
      } else {
        if (router.currentRoute.value.name === 'search') {
          router.back()
          setTimeout(() => {
            if (router.currentRoute.value.name === 'search') {
              router.push({ name: 'home'})
            }
          })
        }
      }
    }

    const doSearch = (event: any) => {
      event.preventDefault()
      const searchField = document.getElementById('search-field') as HTMLInputElement
      const searchString = searchField.value
      if (searchString) {
        router.push({ name: 'search', params: { searchString } })
        searchField.blur()
      }
    }

    const handleQrFileInput = (event: any) => {
      const files: FileList = event.target?.files || []
      const file = files.item(0)
      if (file) {
        const img = new Image();
        img.onload = () => {
          if (!qrCanvasContext || !qrCanvasElement) {
            console.error('Missing QR canvas context or element')
            return
          }
          qrCanvasContext.drawImage(
              img,
              0,
              0,
              qrCanvasElement.width,
              qrCanvasElement.height
          )

          const code = getQrCodeFromCanvas(qrCanvasContext, qrCanvasElement)
          console.log('code:', code) // TEMP
          if (code) {
            turnOffQrScanner()
            actOnQrCodeData(code.data)
          } else {
            alert('We did not find a QR code in that picture. Try again?')
          }
        }
        img.src = URL.createObjectURL(file)
      }
    }

    const toggleQrCodeScanner = (event: any) => {
      event.preventDefault()
      showQrScanner.value = !showQrScanner.value
      if (showQrScanner.value) {
        setTimeout(() => turnOnQrScanner(offerNavigationToQrCodeUrl))
      } else {
        turnOffQrScanner()
      }
    }

    const hideAlternateHomeLayouts = () => {
      showButtonLayout.value = false;
      showWelcomeLayout.value = false;
    }

    const initializeSearchField = () => {
      showSearchField.value = (route.name === 'search')
      if (showSearchField.value) {
        setTimeout(() => {
          const searchField = document.getElementById('search-field') as HTMLInputElement
          searchField && (searchField.value = String(route.params.searchString || ''))
        })
      }
    }
    initializeSearchField()
    watch(
        () => route.name,
        async () => {
          initializeSearchField()

          if (route.name !== 'home') {
            hideAlternateHomeLayouts()
          }
        }
    )

    const exitSubmap = () => {
      const submapGuid = store.state.selectedSubmap
      const submap = store.getSubmap(submapGuid)
      if (submap) {
        const pointGuid = submap.parentPointGuid
        router.push({ name: 'point', params: { pointGuid } })
      }
    }

    const goHome = async () => {
      await router.push({ name: 'home' })
      showConfiguredHomeScreenLayout()
    }

    const handleHideButtonLayout = () => {
      showButtonLayout.value = false;
    }

    const handleHideWelcomeLayout = () => {
      showWelcomeLayout.value = false;
    }

    const handleWelcomeLayoutShowPoint = async (pointGuid: any) => {
      const point = await store.getPointOnceLoaded(pointGuid)
      showWelcomeLayout.value = false;
      if (point.parentSubmap) {
        router.push({ name: 'point-in-submap', params: {
          pointGuid,
          submapGuid: point.parentSubmap,
        } })
      } else {
        router.push({ name: 'point', params: {
          pointGuid
        } })
      }
    }

    const handleWelcomeLayoutShowSubmap = (submapGuid: any) => {
      showWelcomeLayout.value = false;
      router.push({
        name: 'submap', params: {
          submapGuid
        }
      })
    }

    return {
      doSearch,
      exitSubmap,
      goHome,
      handleHideButtonLayout,
      handleHideWelcomeLayout,
      handleQrFileInput,
      handleWelcomeLayoutShowPoint,
      handleWelcomeLayoutShowSubmap,
      showButtonLayout,
      showWelcomeLayout,
      showQrScanner,
      showSearchField,
      toggleQrCodeScanner,
      toggleSearchField,
    }
  }
})
